import React, { useEffect, useState } from 'react';
import { Link2, Copy, AlertTriangle, RefreshCw, Check } from 'lucide-react';
import { apiClient } from '../../lib/api';

interface Props {
  physicianId: string;
}

interface Status {
  enabled: boolean;
  hasSecret: boolean;
  createdAt?: string;
}

interface RevealedSecret {
  secret: string;
  algorithm: string;
  signedParamKeys: string[];
  sampleLink: string; // full URL (origin + path)
}

/**
 * Admin panel for the per-clinic DTP signed-link auto-import. Lets an admin
 * generate/rotate the HMAC secret (shown once) and toggle the feature. The
 * secret is the trust anchor — the clinic signs URL params with it to mint
 * patient sessions without OTP.
 */
export const DtpAutoImportSection: React.FC<Props> = ({ physicianId }) => {
  const [status, setStatus] = useState<Status | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<RevealedSecret | null>(null);
  const [copied, setCopied] = useState<'secret' | 'link' | null>(null);

  const loadStatus = async () => {
    setLoading(true);
    const res = await apiClient.getDtpAutoImportStatus(physicianId);
    setLoading(false);
    if (res.success && res.data) setStatus(res.data);
    else setError(res.error || 'Failed to load auto-import status');
  };

  useEffect(() => {
    setRevealed(null);
    setError(null);
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [physicianId]);

  const handleRotate = async () => {
    if (status?.hasSecret && !window.confirm('Rotate the secret? Every link already issued with the old secret will stop working.')) {
      return;
    }
    setBusy(true);
    setError(null);
    const res = await apiClient.rotateDtpAutoImportSecret(physicianId);
    setBusy(false);
    if (res.success && res.data) {
      setRevealed({
        secret: res.data.secret,
        algorithm: res.data.algorithm,
        signedParamKeys: res.data.signedParamKeys,
        sampleLink: `${window.location.origin}${res.data.sampleLinkPath}`
      });
      loadStatus();
    } else {
      setError(res.error || 'Failed to generate secret');
    }
  };

  const handleToggle = async () => {
    if (!status) return;
    setBusy(true);
    setError(null);
    const res = await apiClient.setDtpAutoImportEnabled(physicianId, !status.enabled);
    setBusy(false);
    if (res.success) setStatus({ ...status, enabled: !status.enabled });
    else setError(res.error || 'Failed to update');
  };

  const copy = (text: string, which: 'secret' | 'link') => {
    navigator.clipboard.writeText(text);
    setCopied(which);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div className="md:col-span-2 border-t border-gray-100 pt-4 mt-2">
      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1.5 mb-2">
        <Link2 className="h-3.5 w-3.5" /> DTP Auto-Import Link (signed)
      </label>

      {loading ? (
        <div className="text-sm text-gray-400">Loading…</div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              status?.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
            }`}>
              {status?.enabled ? 'Enabled' : 'Disabled'}
            </span>
            {status?.hasSecret ? (
              <span className="text-xs text-gray-500">
                Secret set{status.createdAt ? ` ${new Date(status.createdAt).toLocaleDateString()}` : ''}
              </span>
            ) : (
              <span className="text-xs text-gray-400">No secret yet</span>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleRotate}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              {status?.hasSecret ? 'Rotate secret' : 'Generate secret'}
            </button>
            {status?.hasSecret && (
              <button
                type="button"
                onClick={handleToggle}
                disabled={busy}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                {status.enabled ? 'Disable' : 'Enable'}
              </button>
            )}
          </div>

          {error && <div className="text-sm text-red-600">{error}</div>}

          {revealed && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 space-y-3">
              <div className="flex items-start gap-2 text-sm text-amber-900">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>Copy this secret now — it won’t be shown again. Share it only with the clinic’s system over a secure channel.</span>
              </div>

              <div>
                <div className="text-xs font-medium text-gray-600 mb-1">Signing secret</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-white border border-gray-200 rounded px-2 py-1.5 break-all font-mono">{revealed.secret}</code>
                  <button type="button" onClick={() => copy(revealed.secret, 'secret')} className="p-1.5 text-gray-500 hover:text-gray-700" title="Copy secret">
                    {copied === 'secret' ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <div className="text-xs font-medium text-gray-600 mb-1">Sample signed link (7-day expiry)</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-white border border-gray-200 rounded px-2 py-1.5 break-all font-mono">{revealed.sampleLink}</code>
                  <button type="button" onClick={() => copy(revealed.sampleLink, 'link')} className="p-1.5 text-gray-500 hover:text-gray-700" title="Copy link">
                    {copied === 'link' ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <details className="text-xs text-gray-600">
                <summary className="cursor-pointer text-gray-700">Signing spec</summary>
                <p className="mt-1">{revealed.algorithm}</p>
                <p className="mt-1">Signed params: <span className="font-mono">{revealed.signedParamKeys.join(', ')}</span>. Phone is the patient key; new patients also need firstName, lastName, dob, and a full address.</p>
              </details>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DtpAutoImportSection;
